import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CalendarDays, Check, MapPin } from 'lucide-react';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { ErrorState } from '@shared/ui/feedback/ErrorState';
import { EmptyState } from '@shared/ui/feedback/EmptyState';
import { Card } from '@shared/ui/containers/Card';
import { toast } from '@shared/ui/feedback/Toast';
import { useBrandImagery } from '@shared/hooks/useBrandImagery';
import { pageCopy } from '@shared/lib/pageCopy';
import { HeroHeader } from '@shared/ui/headers/HeroHeader';
import { toApiError } from '@shared/api/errors';
import { fetchEvents, rsvpEvent, type EventSummary } from './api';

const DATE_FMT = new Intl.DateTimeFormat(undefined, {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
});
const TIME_FMT = new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' });

type Rsvp = 'yes' | 'no';

/**
 * Events — Fancourt layout: a photo header over a feed of image-led event
 * cards with inline RSVP actions.
 */
export function Events(): JSX.Element {
  const copy = pageCopy().events;
  const queryClient = useQueryClient();
  const { heroImage } = useBrandImagery();

  const range = useMemo(() => {
    const from = new Date();
    const to = new Date();
    to.setMonth(to.getMonth() + 6);
    return { from: from.toISOString(), to: to.toISOString() };
  }, []);

  const eventsQuery = useQuery({
    queryKey: ['events'],
    queryFn: () => fetchEvents(range.from, range.to),
  });

  const rsvp = useMutation({
    mutationFn: ({ id, status }: { id: string; status: Rsvp }) => rsvpEvent(id, status),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['events'] });
      toast.success('RSVP updated');
    },
    onError: (error) => toast.danger('Could not update RSVP', toApiError(error).message),
  });

  const events = [...(eventsQuery.data ?? [])].sort((a, b) => a.starts_at.localeCompare(b.starts_at));

  return (
    <div className="min-h-full bg-paper">
      {/* ── Photo header ─────────────────────────────────────────── */}
      <HeroHeader
        eyebrow={copy.eyebrow}
        title={copy.title}
        subtitle={copy.subtitle}
        backgroundImage={heroImage}
      />

      <div className="flex flex-col gap-4 px-5 pt-5 pb-safe-or-6">
        {eventsQuery.isLoading ? (
          <>
            <Skeleton className="h-72" />
            <Skeleton className="h-72" />
          </>
        ) : eventsQuery.isError ? (
          <ErrorState onRetry={() => eventsQuery.refetch()} />
        ) : events.length === 0 ? (
          <EmptyState
            icon={<CalendarDays className="h-7 w-7" strokeWidth={1.5} />}
            title={copy.emptyTitle}
            description={copy.emptyDescription}
          />
        ) : (
          events.map((event) => (
            <EventCard
              key={event.event_id}
              event={event}
              pending={rsvp.isPending && rsvp.variables?.id === event.event_id}
              onRsvp={(status) => rsvp.mutate({ id: event.event_id, status })}
            />
          ))
        )}
      </div>
    </div>
  );
}

function EventCard({
  event,
  pending,
  onRsvp,
}: {
  event: EventSummary;
  pending: boolean;
  onRsvp: (status: Rsvp) => void;
}): JSX.Element {
  const start = parseEventDate(event.starts_at);

  return (
    <Card asChild noGrain className="overflow-hidden rounded-xl p-0">
      <article>
      {/* Cover */}
      <div className="relative aspect-[16/9] w-full bg-sunken">
        {event.cover_image_url ? (
          <img src={event.cover_image_url} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-primary-50">
            <CalendarDays className="h-8 w-8 text-primary-600" strokeWidth={1.25} />
          </div>
        )}
        {event.category ? (
          <span className="absolute left-3 top-3 rounded-pill bg-black/65 px-2.5 py-1 text-[0.625rem] font-semibold uppercase tracking-[0.12em] text-white backdrop-blur">
            {event.category}
          </span>
        ) : null}
        {!event.all_day && start ? (
          <span className="absolute right-3 top-3 rounded-pill bg-white px-2.5 py-1 text-caption font-semibold text-ink">
            {TIME_FMT.format(start)}
          </span>
        ) : null}
      </div>

      {/* Body */}
      <div className="p-4">
        <h2 className="font-display text-headline text-ink">{event.title}</h2>
        <p className="mt-1.5 flex items-center gap-1.5 text-body-sm text-ink-secondary">
          <CalendarDays className="h-4 w-4 shrink-0 text-ink-muted" strokeWidth={1.5} />
          {start ? DATE_FMT.format(start) : 'Date to be confirmed'}
          {event.all_day ? ' · All day' : ''}
        </p>
        {event.location ? (
          <p className="mt-1 flex items-center gap-1.5 text-body-sm text-ink-secondary">
            <MapPin className="h-4 w-4 shrink-0 text-ink-muted" strokeWidth={1.5} />
            {event.location}
          </p>
        ) : null}

        {/* RSVP */}
        <div className="mt-3 flex gap-2">
          {(['yes', 'no'] as Rsvp[]).map((status) => {
            const active = event.rsvp_status === status;
            const label = status === 'yes' ? 'Going' : 'Decline';
            return (
              <button
                key={status}
                type="button"
                disabled={pending}
                onClick={() => {
                  if (!active) {
                    onRsvp(status);
                  }
                }}
                className={
                  'flex flex-1 items-center justify-center gap-1.5 rounded-md py-2.5 text-body-sm font-medium transition-colors ' +
                  (active
                    ? 'bg-primary-700 text-white'
                    : 'bg-card hairline text-ink-secondary hover:bg-sunken')
                }
              >
                {active && status === 'yes' ? (
                  <Check className="h-4 w-4" strokeWidth={2.5} />
                ) : null}
                {label}
              </button>
            );
          })}
        </div>
      </div>
      </article>
    </Card>
  );
}

function parseEventDate(iso: string): Date | null {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}
